// src/broadcast/hooks/useUserChannelBroadcast.ts
"use client"

import { useEffect, useRef, useCallback } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { createClient } from '@/utils/supabase/client'
import { useBroadcastStore } from '../stores/useBroadcastStore'
import { validateBroadcastPayload, getPayloadType } from '../utils/validator'
import { getChannelName } from '../utils/channel'
import type { BroadcastHookOptions, BroadcastHookReturn } from '../types/base'
import type { MessageBroadcastPayload } from '../types/events/message'

/**
 * Whether to use private channels (requires Supabase Realtime authorization)
 * Set NEXT_PUBLIC_BROADCAST_PRIVATE=false to disable private channels (dev only)
 * Default: true (private channels enabled)
 *
 * For private channels to work, you need to:
 * 1. Enable Realtime Authorization in Supabase Dashboard > Project Settings > API
 * 2. Create RLS policies for the broadcast channel authorization
 *
 * @see https://supabase.com/docs/guides/realtime/authorization
 */
const USE_PRIVATE_CHANNEL = process.env.NEXT_PUBLIC_BROADCAST_PRIVATE !== 'false'

/**
 * Maximum reconnection attempts before giving up
 */
const MAX_RECONNECT_ATTEMPTS = 5

/**
 * Subscription timeout in milliseconds
 * If subscription doesn't complete within this time, consider it failed
 */
const SUBSCRIPTION_TIMEOUT = 30000 // 30 seconds

/**
 * Reconnection delay in milliseconds (exponential backoff)
 */
const getReconnectDelay = (attempt: number) => Math.min(1000 * Math.pow(2, attempt), 30000)

/**
 * Main hook for user broadcast channel subscription
 * Subscribes to user:{userId}:broadcast and handles all event types
 */
export function useUserChannelBroadcast({
  userId,
  enabled = true,
  onMessage,
  onError,
  onConnectionChange,
}: BroadcastHookOptions): BroadcastHookReturn {
  // === REFS (stable across renders) ===
  const channelRef = useRef<RealtimeChannel | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const supabaseRef = useRef(createClient())
  const isSetupInProgressRef = useRef(false)

  // Store callbacks in refs to avoid re-triggering effects
  // This is the React pattern for "I want to read the latest value but not re-run effects"
  const onMessageRef = useRef(onMessage)
  const onErrorRef = useRef(onError)
  const onConnectionChangeRef = useRef(onConnectionChange)

  // Keep refs up to date
  useEffect(() => {
    onMessageRef.current = onMessage
  }, [onMessage])

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  useEffect(() => {
    onConnectionChangeRef.current = onConnectionChange
  }, [onConnectionChange])

  // === STORE ACTIONS (stable from Zustand) ===
  // Use the store's getState pattern for actions to avoid subscription issues
  const getStoreActions = useCallback(() => {
    const state = useBroadcastStore.getState()
    return {
      setConnected: state.setConnected,
      setConnectionError: state.setConnectionError,
      setLastEventTimestamp: state.setLastEventTimestamp,
      incrementReconnectAttempts: state.incrementReconnectAttempts,
      resetReconnectAttempts: state.resetReconnectAttempts,
      addMessage: state.addMessage,
      updateMessage: state.updateMessage,
      removeMessage: state.removeMessage,
    }
  }, [])

  // === STORE STATE (subscribed for reactivity) ===
  const isConnected = useBroadcastStore((s) => s.isConnected)
  const connectionError = useBroadcastStore((s) => s.connectionError)

  /**
   * Handle message events (INSERT, UPDATE, DELETE)
   */
  const handleMessageEvent = useCallback((payload: MessageBroadcastPayload) => {
    const { event } = payload.metadata
    const { record } = payload.metadata.payload
    const { addMessage, updateMessage, removeMessage } = getStoreActions()

    console.log(`[Broadcast] Message ${event}:`, record.message_id)

    switch (event) {
      case 'INSERT':
        addMessage(record)
        break
      case 'UPDATE':
        updateMessage(record.message_id, record)
        break
      case 'DELETE':
        removeMessage(record.message_id)
        break
    }
  }, [getStoreActions])

  /**
   * Dispatch a custom DOM event for non-message broadcast events
   * This allows other hooks (like useAIPromptBroadcast) to subscribe to specific event types
   */
  const dispatchBroadcastEvent = useCallback((eventType: string, payload: unknown) => {
    if (typeof document !== 'undefined') {
      const customEvent = new CustomEvent(`broadcast:${eventType}`, {
        detail: payload,
        bubbles: false,
      })
      document.dispatchEvent(customEvent)
      console.log(`[Broadcast] Dispatched broadcast:${eventType} event`)
    }
  }, [])

  /**
   * Main broadcast event handler
   */
  const handleBroadcast = useCallback((rawPayload: unknown) => {
    const { setLastEventTimestamp } = getStoreActions()

    // Get event type for routing
    const eventType = getPayloadType(rawPayload)

    if (!eventType) {
      console.warn('[Broadcast] Unknown event type:', rawPayload)
      return
    }

    // Update timestamp for all valid events
    if (typeof (rawPayload as any)?.timestamp === 'number') {
      setLastEventTimestamp((rawPayload as any).timestamp)
    }

    // Route based on event type
    switch (eventType) {
      case 'message': {
        const result = validateBroadcastPayload(rawPayload)

        if (!result.success) {
          console.error('[Broadcast] Validation failed:', result.error.errors)
          onErrorRef.current?.(new Error('Invalid message payload'))
          return
        }

        handleMessageEvent(result.data)
        onMessageRef.current?.(result.data)
        break
      }

      case 'image_generation':
        // Dispatch DOM event for image generation - useAIPromptBroadcast listens for these
        dispatchBroadcastEvent('image_generation', rawPayload)
        break

      case 'notification':
      case 'matching':
      case 'system':
        // Future: Add handlers for other event types
        // For now, dispatch DOM events so other hooks can subscribe
        dispatchBroadcastEvent(eventType, rawPayload)
        console.log(`[Broadcast] Received ${eventType} event (handler not implemented)`)
        break
    }
  }, [handleMessageEvent, getStoreActions, dispatchBroadcastEvent])

  /**
   * Main connection effect
   * Dependencies are minimal: only userId and enabled
   * All callbacks are accessed via refs to prevent re-runs
   */
  useEffect(() => {
    // Early exit conditions
    if (!enabled || !userId) {
      return
    }

    // Prevent double setup (React StrictMode)
    if (isSetupInProgressRef.current) {
      return
    }

    // Already connected
    if (String(channelRef.current?.state) === 'subscribed') {
      return
    }

    isSetupInProgressRef.current = true

    const supabase = supabaseRef.current
    const channelName = getChannelName(userId)
    console.log(`[Broadcast] Connecting to ${channelName}`, {
      privateChannel: USE_PRIVATE_CHANNEL
    })

    // Create channel with optional private config
    // Private channels require RLS setup on realtime.messages table
    const channel = USE_PRIVATE_CHANNEL
      ? supabase.channel(channelName, { config: { private: true } })
      : supabase.channel(channelName)
    channelRef.current = channel

    // Subscription timeout ref for cleanup
    let subscriptionTimeoutId: NodeJS.Timeout | null = null

    let reconnectAttempts = 0

    const attemptReconnect = () => {
      const { setConnectionError, incrementReconnectAttempts } = getStoreActions()

      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.error('[Broadcast] Max reconnection attempts reached')
        setConnectionError(new Error('Max reconnection attempts reached'))
        return
      }

      const delay = getReconnectDelay(reconnectAttempts)
      console.log(`[Broadcast] Reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1})`)

      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectAttempts++
        incrementReconnectAttempts()
        // Re-run setup would require more complex logic
        // For now, rely on component remount for reconnection
      }, delay)
    }

    const setupChannel = async () => {
      const { setConnected, setConnectionError, resetReconnectAttempts } = getStoreActions()

      // Track if subscription completed (for timeout cleanup)
      let subscriptionCompleted = false

      // Set subscription timeout - if no status within SUBSCRIPTION_TIMEOUT, fail
      subscriptionTimeoutId = setTimeout(() => {
        if (!subscriptionCompleted) {
          console.error(`[Broadcast] Subscription timeout (${SUBSCRIPTION_TIMEOUT / 1000}s) - channel never connected`)
          setConnectionError(new Error('Subscription timeout - check Supabase Realtime configuration'))
          onConnectionChangeRef.current?.(false)
          onErrorRef.current?.(new Error('Subscription timeout'))
          attemptReconnect()
          isSetupInProgressRef.current = false
        }
      }, SUBSCRIPTION_TIMEOUT)

      try {
        // For private channels, we need to set auth first
        if (USE_PRIVATE_CHANNEL) {
          console.log('[Broadcast] Step 1: Getting session (private channel)...')

          // Add timeout for session retrieval (10 seconds)
          const sessionPromise = supabase.auth.getSession()
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Session retrieval timeout (10s)')), 10000)
          )

          const { data: { session }, error: sessionError } = await Promise.race([
            sessionPromise,
            timeoutPromise
          ]) as Awaited<typeof sessionPromise>

          console.log('[Broadcast] Step 2: Session retrieved', {
            hasSession: !!session,
            hasToken: !!session?.access_token,
            error: sessionError?.message
          })

          if (sessionError) {
            console.error('[Broadcast] Failed to get session:', sessionError.message)
            setConnectionError(new Error(`Auth error: ${sessionError.message}`))
            onErrorRef.current?.(new Error(`Auth error: ${sessionError.message}`))
            isSetupInProgressRef.current = false
            if (subscriptionTimeoutId) clearTimeout(subscriptionTimeoutId)
            return
          }

          if (!session?.access_token) {
            console.error('[Broadcast] No access token available - user may not be authenticated')
            setConnectionError(new Error('No access token available'))
            onErrorRef.current?.(new Error('No access token available'))
            isSetupInProgressRef.current = false
            if (subscriptionTimeoutId) clearTimeout(subscriptionTimeoutId)
            return
          }

          console.log('[Broadcast] Step 3: Setting realtime auth...')

          // Set auth with timeout (5 seconds)
          const setAuthPromise = supabase.realtime.setAuth(session.access_token)
          const setAuthTimeout = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('setAuth timeout (5s)')), 5000)
          )

          await Promise.race([setAuthPromise, setAuthTimeout])
          console.log('[Broadcast] Step 4: Auth token set successfully')
        } else {
          console.log('[Broadcast] Using public channel (no auth required)')
        }

        console.log('[Broadcast] Subscribing to channel...')

        channel
          .on('broadcast', { event: '*' }, ({ payload }) => handleBroadcast(payload))
          .subscribe((status, err) => {
            console.log(`[Broadcast] Status: ${status}`, err ? `Error: ${err}` : '')

            // Mark as completed to prevent timeout handling
            if (status === 'SUBSCRIBED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
              subscriptionCompleted = true
              if (subscriptionTimeoutId) {
                clearTimeout(subscriptionTimeoutId)
                subscriptionTimeoutId = null
              }
            }

            switch (status) {
              case 'SUBSCRIBED':
                console.log('[Broadcast] Successfully subscribed!')
                setConnected(true)
                resetReconnectAttempts()
                reconnectAttempts = 0
                onConnectionChangeRef.current?.(true)
                isSetupInProgressRef.current = false
                break

              case 'CHANNEL_ERROR':
                console.error('[Broadcast] Channel error:', err)
                setConnectionError(err || new Error('Channel error'))
                onConnectionChangeRef.current?.(false)
                onErrorRef.current?.(err || new Error('Channel error'))
                attemptReconnect()
                isSetupInProgressRef.current = false
                break

              case 'TIMED_OUT':
                console.warn('[Broadcast] Connection timed out')
                setConnectionError(new Error('Connection timed out'))
                onConnectionChangeRef.current?.(false)
                attemptReconnect()
                isSetupInProgressRef.current = false
                break

              case 'CLOSED':
                console.log('[Broadcast] Channel closed')
                setConnected(false)
                onConnectionChangeRef.current?.(false)
                isSetupInProgressRef.current = false
                break
            }
          })
      } catch (error) {
        console.error('[Broadcast] Setup error:', error)
        if (subscriptionTimeoutId) {
          clearTimeout(subscriptionTimeoutId)
          subscriptionTimeoutId = null
        }
        setConnectionError(error as Error)
        onErrorRef.current?.(error as Error)
        attemptReconnect()
        isSetupInProgressRef.current = false
      }
    }

    setupChannel()

    // Cleanup - only runs when userId or enabled changes
    return () => {
      console.log(`[Broadcast] Cleaning up ${channelName}`)
      const { setConnected } = getStoreActions()

      // Clear subscription timeout
      if (subscriptionTimeoutId) {
        clearTimeout(subscriptionTimeoutId)
        subscriptionTimeoutId = null
      }

      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }

      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }

      setConnected(false)
      isSetupInProgressRef.current = false
    }
  }, [userId, enabled, handleBroadcast, getStoreActions])
  // Note: handleBroadcast and getStoreActions are stable (memoized with no changing deps)

  /**
   * Send a broadcast message
   */
  const send = useCallback(async (event: string, payload: unknown): Promise<string> => {
    if (!channelRef.current) {
      throw new Error('Channel not initialized')
    }

    if (String(channelRef.current.state) !== 'subscribed') {
      throw new Error(`Channel not connected (state: ${channelRef.current.state})`)
    }

    const result = await channelRef.current.send({
      type: 'broadcast',
      event,
      payload,
    })

    if (result !== 'ok') {
      throw new Error(`Failed to send broadcast: ${result}`)
    }

    return result
  }, [])

  return {
    send,
    isConnected,
    error: connectionError,
    channelState: channelRef.current?.state ?? 'closed',
  }
}
